import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import { space } from '@op/opds.tokens';
import { Message } from '@messageformat/react';
import { List, ListItem, Tile } from '@op/opds.opux';
import highchartsMore from 'highcharts/highcharts-more';
import { useEffect, useState } from 'react';
import {
  boundariesVeryCautious,
  developmentVeryCautious,
  fluctuationVeryCautious,
} from './VeryCautiousData';
import {
  boundariesCautious,
  developmentCautious,
  fluctuationCautious,
} from './CautiousData';
import {
  boundariesModerate,
  developmentModerate,
  fluctuationModerate,
} from './ModerateData';
import {
  boundariesProfitOriented,
  developmentProfitOriented,
  fluctuationProfitOriented,
} from './ProfitOrientedData';
import {
  boundariesVeryProfitOriented,
  developmentVeryProfitOriented,
  fluctuationVeryProfitOriented,
} from './VeryProfitOrientedData';
import { getChartOptionsWithOverrides } from '../../util/defaultOptions';
import { useIsMobile } from '../../util/mediaQuery';

highchartsMore(Highcharts);

interface Props {
  selectedAnswer: string;
}

const TaskOneGraph = (props: Props): JSX.Element | null => {
  const { selectedAnswer } = props;
  const [boundaries, setBoundaries] = useState<number[][]>();
  const [development, setDevelopment] = useState<number[]>();
  const [fluctuation, setFluctuation] = useState<number[]>();
  const isMobile = useIsMobile();

  const options = getChartOptionsWithOverrides({
    accessibility: {
      enabled: false,
    },
    title: {
      text: undefined,
    },
    rangeSelector: {
      enabled: false,
    },
    scrollbar: { enabled: false },
    plotOptions: {
      series: {
        enableMouseTracking: false,
        pointStart: Date.UTC(2025, 1, 1),
        pointInterval: (365 * 24 * 3600 * 1000) / 12,
        marker: {
          enabled: false,
        },
      },
    },

    yAxis: {
      showLastLabel: true,
      showFirstLabel: true,
      opposite: false,
      labels: {
        format: '{value} €',
      },
    },
    xAxis: {
      lineWidth: 0,
      minorGridLineWidth: 0,
      type: 'datetime',
      minorTickLength: 0,
      tickLength: 0,
      units: isMobile ? [['year', [5]]] : [['year', [1]]],
      labels: {
        allowOverlap: false,
      },
    },
    series: [
      {
        data: boundaries,
        type: 'arearange',
        color: '#F0F0F0',
        lineWidth: 1,
        lineColor: '#DCDCDC',
      },
      {
        type: 'line',
        color: '#EE5A00',
        data: development,
      },
      {
        type: 'line',
        color: '#EE5A00',
        data: fluctuation,
        dashStyle: 'ShortDash',
      },
    ],
  });

  useEffect(() => {
    if (selectedAnswer === '0') {
      setBoundaries(boundariesVeryCautious);
      setDevelopment(developmentVeryCautious);
      setFluctuation(fluctuationVeryCautious);
    }
    if (selectedAnswer === '1') {
      setBoundaries(boundariesCautious);
      setDevelopment(developmentCautious);
      setFluctuation(fluctuationCautious);
    }
    if (selectedAnswer === '2') {
      setBoundaries(boundariesModerate);
      setDevelopment(developmentModerate);
      setFluctuation(fluctuationModerate);
    }
    if (selectedAnswer === '3') {
      setBoundaries(boundariesProfitOriented);
      setDevelopment(developmentProfitOriented);
      setFluctuation(fluctuationProfitOriented);
    }
    if (selectedAnswer === '4') {
      setBoundaries(boundariesVeryProfitOriented);
      setDevelopment(developmentVeryProfitOriented);
      setFluctuation(fluctuationVeryProfitOriented);
    }
  }, [selectedAnswer]);

  if (selectedAnswer === '') return null;

  return (
    <Tile style={{ marginTop: space.default }}>
      <h5 className="investoracademy-general-graph-title">
        <Message id="course5.task_one_graph.title" />
      </h5>
      <p
        style={{ display: 'none' }}
        id={`course5-task1-graph-description-${selectedAnswer}`}
      >
        <Message id={`course5.task1.answer.${selectedAnswer}.alt_text`} />
      </p>
      <div
        role="img"
        aria-labelledby={`course5-task1-graph-description-${selectedAnswer}`}
      >
        <p className="investoracademy-general-graph-description">
          <Message id="course5.task_one_graph.subtitle" />
        </p>
        <HighchartsReact highcharts={Highcharts} options={options} immutable />
        <div style={{ textAlign: 'center' }}>
          <List
            border="none"
            size="tiny"
            indented
            style={{ display: 'inline-block', textAlign: 'left' }}
          >
            <ListItem style={{ display: 'inline' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="31"
                  height="20"
                  viewBox="0 0 31 20"
                  fill="none"
                  style={{ marginRight: space.xxs }}
                >
                  <g clipPath="url(#clip0_5515_3087)">
                    <path d="M0.5 10H30.5" stroke="#EE5A00" strokeWidth="2.5" />
                  </g>
                  <defs>
                    <clipPath id="clip0_5515_3087">
                      <rect
                        width="30"
                        height="20"
                        fill="white"
                        transform="translate(0.5)"
                      />
                    </clipPath>
                  </defs>
                </svg>
                <Message id="course5.task_one_graph.value_progress" />
              </div>
            </ListItem>
            <ListItem style={{ display: 'inline' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="31"
                  height="20"
                  viewBox="0 0 31 20"
                  fill="none"
                  style={{ marginRight: space.xxs }}
                >
                  <g clipPath="url(#clip0_5515_3091)">
                    <path
                      d="M0.5 10H30.5"
                      stroke="#EE5A00"
                      strokeWidth="2.5"
                      strokeDasharray="6 3"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_5515_3091">
                      <rect
                        width="30"
                        height="20"
                        fill="white"
                        transform="translate(0.5)"
                      />
                    </clipPath>
                  </defs>
                </svg>
                <Message id="course5.task_one_graph.value_fluctuation" />
              </div>
            </ListItem>
            <ListItem style={{ display: 'inline' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="31"
                  height="20"
                  viewBox="0 0 31 20"
                  fill="none"
                  style={{ marginRight: space.xxs }}
                >
                  <rect
                    x="17"
                    y="4.5"
                    width="11"
                    height="11"
                    fill="#F0F0F0"
                    stroke="#DCDCDC"
                  />
                </svg>
                <Message id="course5.task_one_graph.confidence_interval" />
              </div>
            </ListItem>
          </List>
        </div>
      </div>
    </Tile>
  );
};

export default TaskOneGraph;
